import { type FC, useEffect } from 'react';
import styled from 'styled-components';

import { Divider } from '@/components/Divider';
import { Column, Row } from '@/components/Grid';
import { MainContent } from '@/components/MainContent';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

import { OpenPrintDialogButton } from './buttons/OpenPrintDialogButton';
import { CenteredPageContent } from './components/CenteredPageContent';
import { usePresentationContext } from './PresentationContext';
import { a4MaxWidthInPixels } from './utils/a4MaxWidthInPixels';

export const ReportPrintPage: FC = () => {
  const texts = useTexts();
  const { selectedScreens, setIsPrintPreviewPage } = usePresentationContext();

  useEffect(() => {
    setIsPrintPreviewPage(true);

    return () => {
      setIsPrintPreviewPage(false);
    };
  }, []);

  return (
    <CenteredPageContent data-test="pdf-view-page">
      <MainContent>
        <HideInPrint>
          <WithGap>
            <h1>{texts.pages.presentation.reportPreview.title}</h1>

            <div className="font-body">
              {texts.pages.presentation.reportPreview.description}
            </div>

            <Row>
              <Column $span={8}>
                <StyledSurface>
                  <div className="font-body-small-demibold">
                    {texts.pages.presentation.reportPreview.hintTitle}
                  </div>
                  <div>
                    <div className="font-body-small">
                      {texts.pages.presentation.reportPreview.hint1}
                    </div>
                  </div>
                  <div>
                    <div className="font-body-small">
                      {texts.pages.presentation.reportPreview.hint2}
                    </div>
                  </div>
                </StyledSurface>
              </Column>

              <ButtonColumn $span={4}>
                <OpenPrintDialogButton />
              </ButtonColumn>
            </Row>
          </WithGap>
          <Divider />
        </HideInPrint>

        <ScreensContainer>
          {selectedScreens.map((selectedScreen) => (
            <div key={selectedScreen.id}>
              {selectedScreen.element}
              <HideInPrint>
                <Divider />
              </HideInPrint>
            </div>
          ))}
        </ScreensContainer>
      </MainContent>
    </CenteredPageContent>
  );
};

const StyledSurface = styled(ConnectSurface)`
  line-height: 24px;
`;

const HideInPrint = styled.div`
  @media print {
    display: none;
  }
`;

const ButtonColumn = styled(Column)`
  display: flex;
  justify-content: flex-end;
  align-items: end;
  gap: 16px;
`;

const ScreensContainer = styled.div`
  // Set the max-width to ensure that the screen fits an A4 page.
  // This will also ensure the Highcharts charts is rendered to
  // the correct width. Otherwise, the chart will be rendered too
  // wide and will be cut off when printed.
  ${a4MaxWidthInPixels}

  > div:not(:last-child) {
    @media print {
      page-break-after: always;
    }

    @media screen {
      margin-bottom: 128px;
    }
  }
`;
